import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Tooltip, Link, IconButton } from '@mui/material';
import { ReactComponent as ContactIcon } from '../../../assets/PepWise2.0/contact-phone-black.svg';
import PhoneIcon from '@mui/icons-material/Phone';
import { ReactComponent as InfoIcon } from '../../../assets/icon-info.svg';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ReactComponent as CommingSoonIcon } from '../../../assets/coming-soon-dp.svg';
import Loader from 'components/Loader/loader';
import { ReactComponent as KpiIcon } from '../../../assets/PepWise2.0/DataKPIsAlerts.svg';
import { formatSlaDays } from 'utils/common-util';
import BasicDateFilter from 'components/Dropdown/BasicDateFilter';
import dayjs from 'dayjs';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material/styles';

const getTooltipProps = (colors: any) => ({
  componentsProps: {
    tooltip: {
      sx: {
        backgroundColor: `${colors.background.tooltip} !important`,
        color: colors.text.primary,
      },
    },
    arrow: {
      sx: {
        color: `${colors.background.tooltip} !important`,
      },
    },
  },
});

const getInfoLabelSx = (colors: any) => ({
  fontSize: '13px',
  color: colors.text.secondary,
  fontWeight: 500,
  fontStyle: 'italic',
});

const getInfoValueSx = (colors: any) => ({
  fontSize: '12px',
  color: colors.text.primary,
});

const getCardBaseSx = (colors: any) => ({
  position: 'relative',
  width: '100%',
  minHeight: '175px',
  pl: { xs: '30px', sm: '28px', md: '22px' },
  pr: { xs: '24px', sm: '26px', md: '28px' },
  pt: '14px',
  pb: '14px',
  backgroundColor: colors.background.card,
  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
  clipPath: {
    xs: 'polygon(0 0, 92% 0, 100% 50%, 92% 100%, 0 100%, 3% 50%)',
    sm: 'polygon(0 0, 91% 0, 100% 50%, 91% 100%, 0 100%, 4% 50%)',
    md: 'polygon(0 0, 90% 0, 100% 50%, 90% 100%, 0 100%, 6% 50%)',
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'visible',
});

const getSecondaryCardSx = (colors: any) => ({
  backgroundColor: colors.background.card,
  position: 'relative',
  px: '14px',
  py: '12px',
  width: '100%',
  borderRadius: '4px',
  minHeight: '138px',
  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
});

const clickableSx = {
  cursor: 'pointer',
};

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const getRunningDayIndexes = (status: any): number[] => {
  if (Array.isArray(status?.runningDayIndexes)) {
    return status.runningDayIndexes.filter(
      (value: number) => Number.isInteger(value) && value >= 0 && value <= 6,
    );
  }

  const count =
    status?.runningDays ??
    status?.runningDay ??
    status?.running_days ??
    status?.running_days_count ??
    status?.runningDaysLabel;

  if (count === null || count === undefined || count === '') return [];

  if (typeof count === 'number') {
    return Array.from({ length: Math.max(0, Math.min(7, count)) }, (_, i) => i);
  }

  const match = String(count).match(/\d+/);
  const parsed = match ? Number(match[0]) : 0;
  return Array.from({ length: Math.max(0, Math.min(7, parsed)) }, (_, i) => i);
};

const RunningDaysDots = ({ status, colors }: any) => {
  const activeIndexes = getRunningDayIndexes(status);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        mt: 1,
        flexWrap: 'nowrap',
      }}
    >
      {DAY_LABELS.map((day, index) => {
        const isActive = activeIndexes.includes(index);

        return (
          <Box
            key={`${day}-${index}`}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: 1,
              backgroundColor: isActive
                ? colors.background.runningDayActive
                : colors.background.card,
              color: isActive ? colors.text.inverse : colors.border.runningDay,
              border: `1px solid ${colors.border.runningDay}`,
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            {day}
          </Box>
        );
      })}
    </Box>
  );
};

const ProjectTitleBlock = ({
  status,
  navigateToExecutions,
  colors,
  tooltipProps,
  infoLabelSx,
  infoValueSx,
}: any) => {
  const clickable = !!status?.contactInfo?.programId;
  const hasLink = !!status?.contact?.hyperLink;
  const titleText = status.projectFriendlyName || status.projectName;

  const titleSx = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: colors.text.primary,
    ...(clickable ? clickableSx : {}),
  };

  const actualTimeTooltip = status.actualStartTime && (
    <Tooltip
      arrow
      {...tooltipProps}
      title={
        <>
          <Typography variant="body2" sx={infoLabelSx}>
            Actual Start Time
          </Typography>
          <Typography variant="body2" sx={infoValueSx}>
            {moment(status.actualStartTime).utc().format('MM-DD-YYYY, hh:mm A')}
          </Typography>

          {status.actualCompletionTime && (
            <>
              <Typography
                variant="body2"
                sx={{
                  ...infoLabelSx,
                  mt: '5px',
                }}
              >
                Actual End Time
              </Typography>
              <Typography variant="body2" sx={infoValueSx}>
                {moment(status.actualCompletionTime).utc().format('MM-DD-YYYY, hh:mm A')}
              </Typography>
            </>
          )}
        </>
      }
    >
      <Box
        component="span"
        sx={{
          ml: 0.5,
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
          cursor: 'pointer',
          '& svg': {
            width: 18,
            height: 18,
            display: 'block',
          },
        }}
      >
        <InfoIcon />
      </Box>
    </Tooltip>
  );

  if (!hasLink) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Typography
          variant="bodyText"
          sx={titleSx}
          onClick={() => clickable && navigateToExecutions(status.contact)}
          title={status.projectName}
        >
          {titleText}
        </Typography>
        {actualTimeTooltip}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Link
        href={status.contact?.hyperLink}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          minWidth: 0,
          flex: 1,
        }}
        target="_blank"
        rel="noopener"
        color="inherit"
      >
        <Typography variant="bodyText" sx={titleSx} title={status.projectName}>
          {titleText}
        </Typography>
      </Link>
      {actualTimeTooltip}
    </Box>
  );
};

const SlaBlock = ({ status, colors, tooltipProps, infoLabelSx, infoValueSx }: any) => {
  if (!status.isAvailable || (status.isAvailable && status.isAvailable !== 1)) return null;

  return (
    <>
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: '500', color: colors.text.primary }}>
          {status.totalJobs ? status.totalJobs : '--'}
        </Typography>

        {status.slaScheduleInfo && status.slaScheduleInfo.length > 0 && (
          <Box sx={{ display: 'inline-flex', mr: '14.8px', mt: 1.5 }}>
            <Typography
              sx={{
                mt: '2px',
                fontSize: 8,
                fontWeight: '500',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: colors.text.primary,
              }}
            >
              {formatSlaDays(status.slaScheduleInfo)}
            </Typography>
            <Box ml={0.5} sx={{ alignContent: 'center' }}>
              <KpiIcon width={18} height={18} />
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'inline-block', width: '100%', mt: 1 }}>
        <Typography
          variant="bodyText"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: status.colorCode,
            textTransform: 'uppercase',
            fontWeight: '600',
          }}
        >
          {status.slaStatus}

          {status.actualStartTime && (
            <Tooltip
              arrow
              {...tooltipProps}
              title={
                <>
                  <Typography variant="body2" sx={infoLabelSx}>
                    Actual Start Time
                  </Typography>
                  <Typography variant="body2" sx={infoValueSx}>
                    {moment(status.actualStartTime).utc().format('MM-DD-YYYY, hh:mm A')}
                  </Typography>

                  {status.actualCompletionTime && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          ...infoLabelSx,
                          mt: '5px',
                        }}
                      >
                        Actual End Time
                      </Typography>
                      <Typography variant="body2" sx={infoValueSx}>
                        {moment(status.actualCompletionTime).utc().format('MM-DD-YYYY, hh:mm A')}
                      </Typography>
                    </>
                  )}
                </>
              }
            >
              <Box
                component="span"
                sx={{
                  ml: '5px',
                  display: 'inline-flex',
                  '& svg': {
                    width: 18,
                    height: 18,
                    display: 'block',
                  },
                }}
              >
                <InfoIcon />
              </Box>
            </Tooltip>
          )}
        </Typography>
      </Box>
    </>
  );
};

const PlatformHealthBlock = ({
  status,
  sector,
  colors,
  tooltipProps,
  infoLabelSx,
  infoValueSx,
}: any) => {
  const commonTooltip = (
    <>
      <Typography variant="body2" sx={infoLabelSx}>
        Est. Arrival
      </Typography>
      <Typography variant="body2" sx={infoValueSx}>
        {status.eta || (['APAC', 'FLNA', 'PBNA'].includes(sector) ? 'Aug 30, 2025' : '--')}
      </Typography>
    </>
  );

  const bottomTooltip = (
    <>
      <Typography variant="body2" sx={infoLabelSx}>
        Est. Arrival
      </Typography>
      <Typography variant="body2" sx={infoValueSx}>
        {status?.exeProjectEta ? moment(status?.exeProjectEta).utc().format('MMM DD, YYYY') : '--'}
      </Typography>
    </>
  );

  if ((status.isAvailable || status.isAvailable == 1) && !status.platformHealthLink) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          sx={{
            textAlign: 'center',
            border: `1px solid ${colors.border.muted}`,
            borderRadius: '20px',
            margin: '10px 0 8px',
            padding: '2px 8px',
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '12px',
            backgroundColor: colors.background.statusMuted,
            color: colors.text.inverse,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          COMING SOON
          <Tooltip title={commonTooltip} arrow {...tooltipProps}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.5,
                cursor: 'pointer',
                lineHeight: 0,
                '& svg': {
                  width: 14,
                  height: 14,
                  display: 'block',
                },
                '& svg path': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg circle': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg rect': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
              }}
            >
              <CommingSoonIcon />
            </Box>
          </Tooltip>
        </Typography>

        <RunningDaysDots status={status} colors={colors} />
      </Box>
    );
  }

  if (!status.isAvailable) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          sx={{
            textAlign: 'center',
            border: `1px solid ${colors.border.muted}`,
            borderRadius: '20px',
            margin: '10px 0 8px',
            padding: '2px 8px',
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '12px',
            backgroundColor: colors.background.statusMuted,
            color: colors.text.inverse,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          COMING SOON
          <Tooltip title={bottomTooltip} arrow {...tooltipProps}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.5,
                cursor: 'pointer',
                lineHeight: 0,
                '& svg': {
                  width: 14,
                  height: 14,
                  display: 'block',
                },
                '& svg path': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg circle': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg rect': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
              }}
            >
              <CommingSoonIcon />
            </Box>
          </Tooltip>
        </Typography>

        <RunningDaysDots status={status} colors={colors} />
      </Box>
    );
  }

  return <RunningDaysDots status={status} colors={colors} />;
};

const DataPipelineProjectCard = ({
  programId,
  projectDetails,
  dataCardLoader,
  handleDateChange,
  sector,
}: any) => {
  const theme = useTheme();
  const colors = theme.palette.dataPipeline.accordionWidget;
  const tooltipProps = getTooltipProps(colors);
  const infoLabelSx = getInfoLabelSx(colors);
  const infoValueSx = getInfoValueSx(colors);
  const cardBaseSx = getCardBaseSx(colors);
  const secondaryCardSx = getSecondaryCardSx(colors);

  const minDate = new Date('2025-03-18');
  const pageSize = 4;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<any>(null);

  const totalPages =
    projectDetails.length > pageSize ? Math.ceil(projectDetails.length / pageSize) : 1;

  const currentHops = projectDetails.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const loader = false;
  const navigate = useNavigate();

  useEffect(() => {
    const selectedDateRaw = projectDetails.find((pj: any) =>
      pj.sectors == 'APAC' ? pj.projectName == 'Gold' : pj.projectName,
    )?.scheduleDate;

    setSelectedDate(selectedDateRaw ? moment(selectedDateRaw.split('T')[0]) : null);
  }, [projectDetails]);

  useEffect(() => {
    setCurrentPage(1);
  }, [projectDetails]);

  const navigateToExecutions = (navigateData: any) => {
    console.log('navigate data', navigateData.programName);
  };

  const handleDateSelect = (date: any) => {
    if (!date) return;

    const normalizedDate = moment(date.format('YYYY-MM-DD'), 'YYYY-MM-DD');

    setSelectedDate(normalizedDate);

    handleDateChange({
      scheduleDate: normalizedDate.format('YYYY-MM-DD'),
      programId,
    });
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev: number) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev: number) => prev - 1);
    }
  };

  const handleGoToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <Grid container sx={{ backgroundColor: colors.background.page }}>
      {loader ? (
        <Loader />
      ) : (
        <>
          <Grid
            container
            p={2}
            pb={0}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              flexWrap: 'wrap',
              rowGap: 1.5,
            }}
          >
            <Grid item xs={12} md="auto">
              <BasicDateFilter
                value={selectedDate ? dayjs(selectedDate.format('YYYY-MM-DD')) : null}
                onChange={handleDateSelect}
                minDate={dayjs(minDate)}
                maxDate={dayjs()}
              />
            </Grid>

            {projectDetails.length > 0 && (
              <Grid
                item
                xs={12}
                md="auto"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: { xs: 'flex-end', md: 'center' },
                  gap: 1,
                  flexWrap: 'wrap',
                  flexDirection: { xs: 'column', md: 'row' },
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 300,
                    lineHeight: 1,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    width: { xs: '100%', md: 'auto' },
                    textAlign: { xs: 'right', md: 'left' },
                  }}
                >
                  {`Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                    currentPage * pageSize,
                    projectDetails.length,
                  )} of ${projectDetails.length} hops`}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: { xs: 'flex-end', md: 'flex-start' },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => currentPage > 1 && handleGoToPage(1)}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === 1
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === 1 ? 'default' : 'pointer',
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => currentPage > 1 && handlePrev()}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === 1
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === 1 ? 'default' : 'pointer',
                    }}
                  >
                    <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {Array.from({ length: totalPages }, (_, index) => {
                      const page = index + 1;
                      const isActive = currentPage === page;

                      return (
                        <Box
                          key={page}
                          onClick={() => handleGoToPage(page)}
                          sx={{
                            width: '20px',
                            height: '20px',
                            p: 0.3,
                            borderRadius: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: 1,
                            color: isActive
                              ? colors.pagination.pageActiveText
                              : colors.pagination.pageDefaultText,
                            backgroundColor: isActive
                              ? colors.pagination.pageActiveBackground
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: isActive
                                ? colors.pagination.pageActiveBackground
                                : colors.pagination.pageHoverBackground,
                            },
                          }}
                        >
                          {page}
                        </Box>
                      );
                    })}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => currentPage < totalPages && handleNext()}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === totalPages
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === totalPages ? 'default' : 'pointer',
                    }}
                  >
                    <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => currentPage < totalPages && handleGoToPage(totalPages)}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === totalPages
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === totalPages ? 'default' : 'pointer',
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ width: '100%' }}>
            {dataCardLoader ? (
              <Loader />
            ) : (
              <Grid container p={2} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{ color: colors.text.primary, lineHeight: '1.5', mt: 7, mb: 1.5 }}
                  >
                    Total Pipelines
                  </Typography>
                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{ color: colors.text.primary, pb: 0.7, mb: 0.6 }}
                  >
                    SLA Status
                  </Typography>
                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{ color: colors.text.primary, pb: 0.7, mb: 0.6 }}
                  >
                    Platform Health
                  </Typography>
                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{ color: colors.text.primary }}
                  >
                    Running Days
                  </Typography>
                </Grid>

                <Grid item xs={12} md={10} sx={{ pl: { xs: 1.5, sm: 1.5, md: 0 } }}>
                  <Grid container spacing={2}>
                    {currentHops.map((status: any) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={`${status.projectFriendlyName || ''}-${status.projectName || ''}`}
                        sx={{ display: 'flex' }}
                      >
                        <Box
                          sx={{
                            ...cardBaseSx,
                            opacity: status.isAvailable ? 1 : 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              position: 'relative',
                              mb: 1.2,
                              pl: { xs: '8px', sm: '6px', md: '4px' },
                              pr: status?.contactInfo?.name
                                ? { xs: '36px', sm: '34px', md: '34px' }
                                : '4px',
                              overflow: 'visible',
                            }}
                          >
                            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ width: '100%' }}>
                                <ProjectTitleBlock
                                  status={status}
                                  navigateToExecutions={navigateToExecutions}
                                  colors={colors}
                                  tooltipProps={tooltipProps}
                                  infoLabelSx={infoLabelSx}
                                  infoValueSx={infoValueSx}
                                />
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                mt: 0.8,
                                width: '100%',
                                borderBottom: `1px solid ${colors.border.subtle}`,
                              }}
                            />

                            {status?.contactInfo?.name && (
                              <Tooltip
                                arrow
                                {...tooltipProps}
                                title={
                                  <>
                                    <Typography variant="body2" sx={infoLabelSx}>
                                      Contact Person
                                    </Typography>
                                    <Typography variant="body2" sx={infoValueSx}>
                                      {status?.contactInfo?.name || 'Godwin'}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: colors.text.primary,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {status?.contactInfo?.email}
                                    </Typography>
                                    {status?.contactInfo?.phone && (
                                      <Typography variant="body2" sx={infoValueSx}>
                                        <PhoneIcon
                                          sx={{
                                            verticalAlign: 'middle',
                                            width: '15px',
                                            height: '15px',
                                            color: 'blue',
                                          }}
                                        />{' '}
                                        {status?.contactInfo?.phone}
                                      </Typography>
                                    )}
                                  </>
                                }
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    position: 'absolute',
                                    right: { xs: 60, sm: 50, md: 35 },
                                    top: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <ContactIcon />
                                </Box>
                              </Tooltip>
                            )}
                          </Box>

                          {status.contact?.hyperLink ? (
                            <Link
                              href={status.contact.hyperLink}
                              sx={{
                                textDecoration: 'none',
                                p: 0,
                                color: 'inherit',
                                ...(status?.contactInfo?.programId ? clickableSx : {}),
                              }}
                              target="_blank"
                              rel="noopener"
                              color="inherit"
                            >
                              <SlaBlock
                                status={status}
                                colors={colors}
                                tooltipProps={tooltipProps}
                                infoLabelSx={infoLabelSx}
                                infoValueSx={infoValueSx}
                              />
                              <PlatformHealthBlock
                                status={status}
                                sector={sector}
                                colors={colors}
                                tooltipProps={tooltipProps}
                                infoLabelSx={infoLabelSx}
                                infoValueSx={infoValueSx}
                              />
                            </Link>
                          ) : (
                            <Box
                              onClick={() =>
                                status?.contactInfo?.programId &&
                                navigateToExecutions(status.contact)
                              }
                              sx={status?.contactInfo?.programId ? clickableSx : undefined}
                            >
                              <SlaBlock
                                status={status}
                                colors={colors}
                                tooltipProps={tooltipProps}
                                infoLabelSx={infoLabelSx}
                                infoValueSx={infoValueSx}
                              />
                              <PlatformHealthBlock
                                status={status}
                                sector={sector}
                                colors={colors}
                                tooltipProps={tooltipProps}
                                infoLabelSx={infoLabelSx}
                                infoValueSx={infoValueSx}
                              />
                            </Box>
                          )}
                        </Box>

                        {status.platformHealthLink && (
                          <Box
                            sx={{
                              position: 'relative',
                              top: '-17%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 10,
                              textAlign: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                textAlign: 'center',
                                margin: '8px 8px 6px 10px',
                                padding: '2px 0',
                                fontSize: '12px',
                                width: '100px',
                                cursor: 'pointer',
                                borderRadius: '20px',
                                border: `1px solid ${colors.border.muted}`,
                                backgroundColor: colors.background.statusMuted,
                                color: colors.text.inverse,
                                display: 'inline-block',
                                fontWeight: '500',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  status.platformHealthLink,
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                              }}
                            >
                              VIEW HEALTH
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid container p={2} pb={0}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{ borderTop: `1px solid ${colors.border.subtle}`, pt: '15px' }}
                />
              </Grid>
            </Grid>

            <Grid container p={2} spacing={2}>
              <Grid item xs={12} md={2}>
                <Typography variant="widgetTitle" sx={{ color: colors.text.secondary }}>
                  Historic Trend
                </Typography>
                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{ color: colors.text.primary, mt: 1.8, mb: 0.6 }}
                >
                  SLA Type
                </Typography>
                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{ color: colors.text.primary, mb: 0.6 }}
                >
                  Last 7 days
                </Typography>
                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{ color: colors.text.primary, mb: 0.6 }}
                >
                  Last 30 days
                </Typography>
                <Typography variant="bodyText" display="block" sx={{ color: colors.text.primary }}>
                  Last 90 days
                </Typography>
              </Grid>

              <Grid item xs={12} md={10} sx={{ pl: { xs: 1.5, sm: 1.5, md: 0 } }}>
                <Grid container spacing={2}>
                  {currentHops.map((status: any, index: number) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        key={`${status.projectName}-${index}`}
                        sx={{ display: 'flex' }}
                      >
                        <Grid
                          container
                          sx={{
                            ...secondaryCardSx,
                            opacity: !status.isAvailable ? 0.5 : 1,
                          }}
                        >
                          <Box
                            mb={1}
                            sx={{ display: 'inline-block', width: '100%', position: 'relative' }}
                          >
                            <Typography
                              variant="bodyText"
                              display="block"
                              sx={{
                                borderBottom: `1px solid ${colors.border.subtle}`,
                                ml: '10px',
                                pl: '5px',
                                width: '90%',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                color: colors.text.primary,
                              }}
                            >
                              {status.projectFriendlyName || status.projectName}
                            </Typography>
                          </Box>

                          <Grid container>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'inline-block', width: '100%' }}>
                                <Typography
                                  variant="bodyText"
                                  display="block"
                                  sx={{
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: colors.text.secondary,
                                  }}
                                >
                                  SLA Met
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'inline-block', width: '100%' }}>
                                <Typography
                                  variant="bodyText"
                                  display="block"
                                  sx={{
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: colors.text.secondary,
                                  }}
                                >
                                  SLA Missed
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {(status.isAvailable || status.isAvailable == 1) && (
                            <>
                              <Grid container>
                                {status.isAvailable &&
                                  status.slaTrend.last7days.map((count: number, idx: number) => {
                                    return (
                                      <Grid
                                        item
                                        xs={6}
                                        key={`7d-${status.projectFriendlyName}-${idx}`}
                                      >
                                        <Box sx={{ display: 'inline-block', width: '100%' }}>
                                          <Typography
                                            sx={{
                                              textAlign: 'center',
                                              fontWeight: '500',
                                              color: colors.text.primary,
                                            }}
                                          >
                                            {count || 0}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                              </Grid>

                              <Grid container>
                                {status.isAvailable &&
                                  status.slaTrend.last30days.map((count: number, idx: number) => {
                                    return (
                                      <Grid
                                        item
                                        xs={6}
                                        key={`30d-${status.projectFriendlyName}-${idx}`}
                                      >
                                        <Box sx={{ display: 'inline-block', width: '100%' }}>
                                          <Typography
                                            sx={{
                                              textAlign: 'center',
                                              fontWeight: '500',
                                              color: colors.text.primary,
                                            }}
                                          >
                                            {count || 0}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                              </Grid>

                              <Grid container>
                                {status.isAvailable &&
                                  status.slaTrend.last90days.map((count: number, idx: number) => {
                                    return (
                                      <Grid
                                        item
                                        xs={6}
                                        key={`90d-${status.projectFriendlyName}-${idx}`}
                                      >
                                        <Box sx={{ display: 'inline-block', width: '100%' }}>
                                          <Typography
                                            sx={{
                                              textAlign: 'center',
                                              fontWeight: '500',
                                              color: colors.text.primary,
                                            }}
                                          >
                                            {count || 0}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                              </Grid>
                            </>
                          )}

                          {!status.isAvailable && (
                            <Grid container>
                              <Grid item xs={12}>
                                <Typography
                                  sx={{
                                    textAlign: 'center',
                                    margin: '26px auto 27px',
                                    padding: '2px 0',
                                    width: '130px',
                                    fontSize: '12px',
                                    color: colors.text.primary,
                                  }}
                                >
                                  COMING SOON{' '}
                                  <Tooltip
                                    arrow
                                    {...tooltipProps}
                                    title={
                                      <>
                                        <Typography variant="body2" sx={infoLabelSx}>
                                          Est. Arrival
                                        </Typography>
                                        <Typography variant="body2" sx={infoValueSx}>
                                          {status?.exeProjectEta
                                            ? moment(status?.exeProjectEta)
                                                .utc()
                                                .format('MMM DD, YYYY')
                                            : '--'}
                                        </Typography>
                                      </>
                                    }
                                  >
                                    <Box
                                      component="span"
                                      sx={{ verticalAlign: 'text-bottom', display: 'inline-flex' }}
                                    >
                                      <CommingSoonIcon />
                                    </Box>
                                  </Tooltip>
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Grid>
  );
};

export default DataPipelineProjectCard;
